import { Select } from "@result/rui/components";
import { useUpdateOnlyEffect } from "@result/rui/hooks";
import PropTypes from "prop-types";

import def from "#/def/lookup-tables.schema.gen";
import { useLookupTableList } from "#/hooks/useLookupTables";
import ANIMAL_GENDER from "#/model/AnimalGenderCodeList";
import { useTranslation } from "react-i18next";

/**
 * Filtering by lookup table item
 *
 * Example:
 * <SelectLookupTableItem ident={SifStatusReje} formField={getField("status")} />
 *
 * Hint: set optionValue={null} to get entire item object as value
 *
 * @param {string} ident Lookup table ident
 * @param label
 * @param {string|function} optionLabel Field name or function to extract option label
 * @param {string} optionValue Field name to extract option value
 * @param {function} filter Additional filter function to apply on items
 * @param sort
 * @param {boolean} gender Whether to filter items applicable
 * @param props
 */
export default function SelectLookupTableItem({
  ident,
  label,
  optionLabel = "naziv",
  optionValue = "sifra",
  filter = () => true,
  sort,
  gender,
  skipEnvironmentFilter = false,
  ...props
}) {
  const { t } = useTranslation();
  // validates ident and fetches the lookup table list
  //
  // allow passing custom dataLayer for better control
  // in special curcumstances, e.g. in admin pages where we want
  // to use connected mode regardless of the app's online status
  const options = useLookupTableList(ident, props.dataLayer, { skipEnvironmentFilter })
    .filter(filter)
    .filter((item) => {
      switch (true) {
        case gender === ANIMAL_GENDER.ZENSKI.id:
          return item.veljaZaSamice;
        case gender === ANIMAL_GENDER.MOSKI.id:
          return item.veljaZaSamce;
        default:
          return true;
      }
    });

  if (sort) {
    options.sort(sort);
  }

  // if no label is provided, use the lookup table's name from definition
  // fallback to ident if name not present
  const $label = label === null ? null : label || def[ident].name || ident;

  // we could go through useTranslation()'s i18n.language
  // to extract current language, but it is highly
  // unlikely that this component would be used
  // outside of Slovenian locale context
  const lang = "sl";

  let $optionLabel = optionLabel;
  if (
    typeof optionLabel === "string" &&
    ["naziv", "opis"].includes(optionLabel)
  ) {
    // handle i18n fields
    //
    // e.g. optionLabel="naziv" => optionLabel = (item) => item?.naziv?.[lang] || item.id;
    // fallback to sifra/id if name not present
    $optionLabel = (item) => {
      let label = item?.[optionLabel]?.[lang];
      if (label == null || label === "") {
        label = item.sifra ?? item.id;
      }
      return label;
    };
  }

  useUpdateOnlyEffect(() => {
    if (!gender) return;

    const field = props.formField;
    if (!field) return;

    // While the lookup table is still loading there is nothing to validate
    // against, and `some()` over an empty list always fails. Clearing here
    // would wipe a perfectly valid value the moment `gender` hydrates from a
    // late-arriving animal record.
    if (options.length === 0) return;

    const { value } = field;

    const isValid = options.some((item) =>
      optionValue ? item[optionValue] === value : item === value
    );

    if (!isValid && value != null) {
      field.setValue(null);
    }
  }, [gender]);

  return (
    <Select
      optionValue={optionValue}
      label={$label}
      optionId="sifra"
      optionKey="numericId"
      optionLabel={$optionLabel}
      options={options}
      placeholder={props.placeholder ?? t("form.selectOption")}
      {...props}
    />
  );
}

SelectLookupTableItem.propTypes = {
  /**
   * Lookup table ident
   */
  ident: PropTypes.string.isRequired,

  /**
   * Input label
   */
  optionLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /**
   * Field name to extract option value
   */
  optionValue: PropTypes.string,

  /**
   * Additional filter function to apply on items
   */
  filter: PropTypes.func,

  /**
   * Whether to filter items applicable
   */
  female: PropTypes.bool,

  /**
   * Whether to filter items applicable
   */
  male: PropTypes.bool,

  ...Select.propTypes,
};
